/** @module Auth0TokenStorageHelper */
/* eslint-disable import/extensions */
import Auth0CookieHelper from '../../../utils/auth0-cookie-helper.mjs';

/**
 * @typedef {Object} TokenStorageConfig
 * @property {'cookie'} storageType - Where to store the token (only 'cookie' is supported)
 * @property {string} accessTokenName - Name/key for the access token
 * @property {string} [refreshTokenName] - Name/key for the refresh token (optional)
 * @property {string} [idTokenName] - Name/key for the ID token (optional)
 *
 * NOTE: localStorage is NOT supported because SSAS runs on a different subdomain than client apps,
 * and localStorage is origin-scoped (cannot be shared across subdomains). For localStorage storage,
 * use the webhook post-login handler with storageType='none' and have your app store tokens
 * in its own localStorage when it receives the webhook callback.
 */

/**
 * @typedef {Object} TokenData
 * @property {string} accessToken - The access token
 * @property {string} [refreshToken] - The refresh token (optional)
 * @property {string} [idToken] - The ID token (optional)
 */

/**
 * Helper class for storing tokens in various locations.
 * Post-login handlers can use this to store tokens according to their specific needs.
 */
class Auth0TokenStorageHelper {
  /**
   * Default token storage configuration for legacy ShipStation apps.
   * Stores access token in two cookies: _lat (v2) and _latv3 (v3).
   * @type {TokenStorageConfig[]}
   */
  static DEFAULT_TOKEN_STORAGE_CONFIG = [
    { storageType: 'cookie', accessTokenName: '_lat' },
    { storageType: 'cookie', accessTokenName: '_latv3' },
  ];

  /**
   * Stores tokens according to the provided configuration.
   *
   * @param {TokenData} tokens - The tokens to store
   * @param {TokenStorageConfig[]} [tokenStorageConfig] - Configuration for where to store the tokens.
   *   Defaults to DEFAULT_TOKEN_STORAGE_CONFIG (legacy _lat and _latv3 cookies).
   */
  static storeTokens(tokens, tokenStorageConfig = Auth0TokenStorageHelper.DEFAULT_TOKEN_STORAGE_CONFIG) {
    console.log(`Auth0TokenStorageHelper.storeTokens => config: ${JSON.stringify(tokenStorageConfig)}`);
    if (!tokens || !tokens.accessToken) {
      console.warn('Auth0TokenStorageHelper.storeTokens => No access token provided, skipping storage');
      return;
    }

    const { accessToken, refreshToken, idToken } = tokens;
    const cookieExpiry = Auth0CookieHelper.getExpirationFromDays(1);

    tokenStorageConfig.forEach((config) => {
      const {
        storageType, accessTokenName, refreshTokenName, idTokenName, cookieDomain,
      } = config;

      if (storageType === 'cookie') {
        // cookieDomain overrides the default page-derived domain (useful when Auth0 runs on a
        // different TLD than the app — e.g. .shipstation.com CDN vs .sslocal.com app in intg).
        const cookieOpts = cookieDomain
          ? { expiry: cookieExpiry, domain: `domain=${cookieDomain}` }
          : { expiry: cookieExpiry };

        // Store access token in cookie
        if (accessTokenName && accessToken) {
          Auth0CookieHelper.setCookie(accessTokenName, accessToken, cookieOpts);
          console.log(`Auth0TokenStorageHelper => stored accessToken in cookie: ${accessTokenName}`);
        }
        // Store refresh token in cookie (only if configured and available)
        if (refreshTokenName && refreshToken) {
          Auth0CookieHelper.setCookie(refreshTokenName, refreshToken, cookieOpts);
          console.log(`Auth0TokenStorageHelper => stored refreshToken in cookie: ${refreshTokenName}`);
        }
        // Store ID token in cookie (only if configured and available)
        if (idTokenName && idToken) {
          Auth0CookieHelper.setCookie(idTokenName, idToken, cookieOpts);
          console.log(`Auth0TokenStorageHelper => stored idToken in cookie: ${idTokenName}`);
        }
      } else {
        console.warn(`Auth0TokenStorageHelper => Unknown storage type: ${storageType}`);
      }
    });
  }

  /**
   * Convenience method to store tokens using the default ShipStation configuration.
   * This is what the sshome handler and other ShipStation handlers should use.
   *
   * @param {TokenData} tokens - The tokens to store
   */
  static storeTokensDefault(tokens) {
    Auth0TokenStorageHelper.storeTokens(tokens, Auth0TokenStorageHelper.DEFAULT_TOKEN_STORAGE_CONFIG);
  }
}

export default Auth0TokenStorageHelper;
